import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./lobby.css";
import { syncJoinLobbyFromUrlSearch } from "../../utils/joinLobbyFromUrl";

const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";

function buildLobbyName() {
  const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `LOBBY-${rand}`;
}

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function normalizePlayerName(user) {
  if (!user || typeof user !== "object") return "";
  const raw = user.name;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t && !/^null(\s+null)*$/i.test(t)) return t;
  }
  const fn = user.firstName ?? user.first_name ?? "";
  const ln = user.lastName ?? user.last_name ?? "";
  const parts = [fn, ln].filter(
    (p) =>
      p != null &&
      String(p).trim() !== "" &&
      String(p).trim().toLowerCase() !== "null"
  );
  return parts.map((p) => String(p).trim()).join(" ").trim();
}

function resolveJoinLobbyName() {
  try {
    const fromUrl = syncJoinLobbyFromUrlSearch(window.location.search);
    if (fromUrl) return fromUrl;
  } catch {
    // ignore
  }
  try {
    const stored = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (stored && stored.trim()) return stored.trim();
  } catch {
    // ignore storage failures
  }
  return "";
}

/**
 * CREATE folder — white filled, tab top-LEFT, slant on tab's RIGHT edge.
 *
 * viewBox: 0 0 300 240
 * Tab: x=0..148 at y=0, height=36px. Slant: (148,0)→(168,36).
 * Body: full width 300, y=36..240, rounded corners (r=12).
 */
function CreateFolderSvg() {
  return (
    <svg
      className="folderSvg"
      viewBox="0 0 300 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
    >
      <path
        d="
          M12,0
          H148
          L168,36
          H288 Q300,36 300,48
          V228 Q300,240 288,240
          H12 Q0,240 0,228
          V12 Q0,0 12,0 Z
        "
        fill="white"
      />
    </svg>
  );
}

/**
 * JOIN folder — outline only, tab top-RIGHT, slant on tab's LEFT edge.
 *
 * viewBox: 0 0 300 240
 * Tab: x=146..300 at y=0, height=36px. Slant: (126,36)→(146,0).
 * Body: full width 300, y=36..240, rounded corners (r=12).
 *
 * Path clockwise from body bottom-left:
 *   body bottom-left → up left side → body top-left corner →
 *   across to slant base (126,36) → slant up to tab top-left (146,0) →
 *   across tab top to top-right (288,0) → round corner →
 *   down right side to bottom-right (288,240) → round corner →
 *   across bottom to bottom-left (12,240) → round corner → close
 */
function JoinFolderSvg() {
  return (
    <svg
      className="folderSvg"
      viewBox="0 0 300 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
    >
      <path
        d="
          M12,240
          Q0,240 0,228
          V48 Q0,36 12,36
          H126
          L146,0
          H288 Q300,0 300,12
          V228 Q300,240 288,240
          Z
        "
        fill="none"
        stroke="rgba(255,255,255,0.85)"
        strokeWidth="2.5"
      />
    </svg>
  );
}

export default function Lobby() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const storedUserData = useMemo(() => readStoredUserData(), []);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [joining, setJoining] = useState(false);

  const handleCreate = () => {
    const lobbyName = buildLobbyName();
    try {
      sessionStorage.setItem(CURRENT_LOBBY_KEY, lobbyName);
    } catch {
      // ignore storage write failures
    }
    navigate("/content", { state: { lobbyName } });
  };

  const handleNext = async () => {
    if (!pin.trim()) {
      setPinError("Please enter a PIN");
      return;
    }
    setPinError("");
    const userId =
      storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sessionId = storedUserData.sessionId;
    const organizationId = storedUserData.organizationId;
    const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
      /\/+$/,
      ""
    );

    if (!backendBase || !adminToken || !userId || !sessionId || !organizationId) {
      setPinError("Missing session details. Please login again.");
      return;
    }

    const playerName = normalizePlayerName(storedUserData);
    const roleForApi =
      storedUserData.user_type ||
      storedUserData.userType ||
      storedUserData.role ||
      (storedUserData.source === "DEMO" ? "demobypass" : null) ||
      storedUserData.source ||
      "ORG_USER";

    const pinTrim = pin.trim();
    const pinNum = Number(pinTrim);
    const loginPinBody =
      pinTrim !== "" && Number.isFinite(pinNum) ? { login_pin: Math.trunc(pinNum) } : {};
    const joinLobbyHint = resolveJoinLobbyName();
    let matchedLobbyName = "";

    try {
      setJoining(true);
      const res = await fetch(`${backendBase}/addContent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId,
          sessionId,
          organizationId,
          email: storedUserData.email || "",
          emp_id: storedUserData.employeeId || storedUserData.emp_id || "",
          name: playerName,
          reportId: storedUserData.reportId || storedUserData.reportid || "",
          gameId: storedUserData.gameId || "",
          user_type: roleForApi,
          ...(joinLobbyHint ? { lobby: joinLobbyHint } : {}),
          ...loginPinBody,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "Could not initialize user.");
      }
      const joinedLobby = typeof data.lobby === "string" ? data.lobby.trim() : "";
      if (!joinedLobby) {
        await Swal.fire({
          icon: "error",
          text: "Wrong lobby pin",
          confirmButtonText: "OK",
        });
        setJoining(false);
        return;
      }
      matchedLobbyName = joinedLobby;
      try {
        sessionStorage.setItem(CURRENT_LOBBY_KEY, matchedLobbyName);
      } catch {
        // ignore
      }
      try {
        const raw =
          localStorage.getItem("userData") ||
          sessionStorage.getItem("userData") ||
          "{}";
        const parsed = JSON.parse(raw) || {};
        const merged = { ...parsed, lobby: matchedLobbyName };
        localStorage.setItem("userData", JSON.stringify(merged));
        sessionStorage.setItem("userData", JSON.stringify(merged));
      } catch {
        // ignore storage failures
      }
    } catch (err) {
      const msg = String(err?.message || "").trim();
      if (!msg || /pin|lobby|join/i.test(msg)) {
        await Swal.fire({
          icon: "error",
          text: "Wrong lobby pin",
          confirmButtonText: "OK",
        });
      } else {
        setPinError(msg || "Could not join game.");
      }
      setJoining(false);
      return;
    }

    setJoining(false);
    navigate("/game", { state: { pin, lobbyName: matchedLobbyName } });
  };

  useEffect(() => {
    document.documentElement.classList.add("lobby-static-bg");
    document.body.classList.add("lobby-static-bg");
    return () => {
      document.documentElement.classList.remove("lobby-static-bg");
      document.body.classList.remove("lobby-static-bg");
    };
  }, []);

  return (
    <div className="page">
      <div className="gridOverlay" />
      <div className="splitOverlay" />

      <h1 className="heading">LOBBY</h1>

      <div className="cardsRow">

        {/* ── CREATE FOLDER — white fill, tab top-left, slant right edge of tab ── */}
        <div className="createFolder">
          <CreateFolderSvg />
          <span className="folderTabLabel create1">CREATE</span>
          <div className="folderContent">
            <button className="createBtn" onClick={handleCreate}>
              create
            </button>
          </div>
        </div>

        {/* ── JOIN FOLDER — outline only, tab top-right, slant left edge of tab ── */}
        <div className="joinFolder">
          <JoinFolderSvg />
          <span className="folderTabLabel join1">JOIN</span>
          <div className="folderContent">
            <div className="fullWidth">
              <input
                className="joinInput"
                type="text"
                placeholder="Enter PIN"
                value={pin}
                maxLength={8}
                onChange={(e) => {
                  setPin(e.target.value);
                  setPinError("");
                }}
              />
              {pinError && <p className="errorText">{pinError}</p>}
            </div>
          </div>
        </div>

      </div>

      <button className="nextBtn" onClick={handleNext} disabled={joining}>
        {joining ? "Joining..." : "Next"}
      </button>
    </div>
  );
}
